import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Download, Info } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const DocumentDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const { data: template, isLoading } = useQuery({
    queryKey: ['template', id],
    queryFn: async () => {
      console.log('Fetching template with id:', id);
      const { data, error } = await supabase
        .from('templates')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) {
        console.error('Error fetching template:', error);
        throw error;
      }
      
      if (!data) {
        throw new Error('Template not found');
      }
      
      return data;
    }
  });

  const handleDownload = async () => {
    if (!template) return;

    try {
      console.log('Download request initiated');
      console.log('File path:', template.file_path);
      
      const { data, error } = await supabase.storage
        .from('templates')
        .download(template.file_path);

      if (error) {
        console.error('Download error:', error);
        toast({
          title: "Error",
          description: "Could not download the file",
          variant: "destructive",
        });
        return;
      }

      if (data) {
        const blob = data;
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const filename = template.file_path.split('/').pop() || template.file_path;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast({
          title: "Success",
          description: "Download started",
        });
      }
    } catch (error) {
      console.error('Download error:', error);
      toast({
        title: "Error",
        description: "Failed to download file",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white p-8">
        <Button 
          onClick={() => navigate(-1)} 
          className="mb-8 bg-gradient-to-r from-[#3887FF] to-[#9F2BF6] hover:opacity-90 text-white px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="text-center">Loading...</div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-white p-8">
        <Button 
          onClick={() => navigate(-1)} 
          className="mb-8 bg-gradient-to-r from-[#3887FF] to-[#9F2BF6] hover:opacity-90 text-white px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        <div className="text-center">Document not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
        <Button 
          onClick={() => navigate(-1)} 
          className="mb-8 bg-gradient-to-r from-[#3887FF] to-[#9F2BF6] hover:opacity-90 text-white px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>

        <Card className="w-full bg-card-gradient shadow-sm border border-gray-100">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-primary">
              {template.title}
            </CardTitle>
            <p className="text-sm text-muted-foreground">{template.type}</p>
          </CardHeader>
          
          <CardContent>
            <p className="text-sm text-gray-600 mb-6">{template.description}</p>
            
            <div className="flex items-start gap-2 text-sm text-secondary/80 bg-blue-50 p-4 rounded-lg">
              <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <p>
                Note: This template has been collected from publicly available resources. 
                It is provided as-is for your convenience and reference.
              </p>
            </div>
          </CardContent>

          <CardFooter>
            <Button
              onClick={handleDownload}
              className="w-full bg-gradient-to-r from-[#3887FF] to-[#9F2BF6] hover:opacity-90 text-white px-4 py-2 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              <Download className="h-4 w-4 mr-2" />
              Download Template
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
};

export default DocumentDetail;
